import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import WPDropdown from "../../packages/WPDropdown";
import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import { APP_SCOPE, CustomModalButtons } from "../../constants/global.constants";
import {
  updateSelectedTrain,
  storePlantDetails,
  storeCleanData,
  storeDefaultInformation,
  storeFileId,
  storeUnits,
  storePlantData,
  storeReplacementThreshold,
  storeTrainUtilityCosts,
  storeTrainChemicalSelection,
} from "../../Store/TemplateStore/template.action";
import { getLastFileDetails, getProceedToAnalysis } from "../../network/cipAPI";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../Notifications";
import {
  getDatasetDetails,
  getSelectedPlantTrainInfo,
  extractPlantId,
  updatePlantDataTrain,
} from "../../utils/Utils";
import moment from "moment";
import { ValueStrings } from "../../utils/constantsString";

import "./index.scss";

const TrainSelector = ({ disabled = false, app = APP_SCOPE.CA }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { num_trains, plantData } = useSelector((state) => ({
    num_trains: state.num_trains,
    plantData: state.plantData,
  }));
  const currentPlantId = extractPlantId();
  const trainToDisplay = getSelectedPlantTrainInfo(plantData, currentPlantId);
  const [selectedTrain, setSelectedTrain] = useState(`Train ${trainToDisplay}`);
  const [showSwitchPopup, setShowSwitchPopup] = useState(false);
  const [pendingTrain, setPendingTrain] = useState("");
  const [isSwitchingTrain, setIsSwitchingTrain] = useState(false);

  // Show error notification helper
  const showErrorNotification = (message) => {
    createNotification(
      ERROR_NOTIFICATION,
      message || t("selectedTrainInfo.unexpectedError"),
      t("selectedTrainInfo.unableToSwitchTrain"),
    );
  };

  useEffect(() => {
    setSelectedTrain(`${t("ValueStrings.train")} ${trainToDisplay}`);
  }, [t, trainToDisplay]);

  const handleTrainChange = (option) => {
    const newTrain = option?.value;
    if (newTrain && newTrain !== selectedTrain) {
      setPendingTrain(newTrain);
      setShowSwitchPopup(true);
    }
  };

  const fetchTrainDetails = async (trainNumber, shouldUpdateRedux = true) => {
    const fileDetails = await getLastFileDetails(trainNumber, {
      overrideAppScope: app,
    });
    if (!fileDetails?.data?.file?.file_id) {
      throw new Error(`Failed to get file_id for train ${trainNumber}`);
    }
    if (shouldUpdateRedux && fileDetails?.data) {
      const data = fileDetails.data;
      const {
        owner_name,
        city,
        country,
        name,
        water_type,
        num_trains,
        tol_num_elements,
        sel_pass,
        sel_stage,
        sel_pressure_vessels,
        sel_element_involved,
        file,
        sel_element_type,
        is_dupont_element,
        utility_costs,
        default_cip: defaultInfo,
        setting: settings,
        installation_date,
        warranty,
        threshold,
        chemical_selection,
      } = data;

      // Store plant details
      const plantInformation = {
        plantLocation: `${city ? ` ${city}, ${country}` : `${country}`}`,
        plantName: name,
        water_type,
        num_trains,
        tol_num_elements,
        sel_train: trainNumber,
        sel_pass,
        sel_stage,
        sel_pressure_vessels,
        sel_element_involved,
        dataset_name: getDatasetDetails({
          owner_username: owner_name,
          min_date: file?.min_date,
          max_date: file?.max_date,
        }),
        sel_element_type,
        is_dupont_element: is_dupont_element ? "No" : "Yes",
        installation_date: installation_date
          ? moment(installation_date).format(
              ValueStrings.installationDateFormat,
            )
          : "",
        warranty,
      };
      dispatch(storePlantDetails(plantInformation));
      dispatch(updateSelectedTrain(trainNumber));

      // Update sel_train in plantData array
      dispatch(
        storePlantData(
          updatePlantDataTrain(plantData, currentPlantId, trainNumber),
        ),
      );

      if (chemical_selection) {
        dispatch(storeTrainChemicalSelection(chemical_selection));
      }

      if (utility_costs) {
        dispatch(storeTrainUtilityCosts(utility_costs));
      }

      // Store default CIP information
      if (defaultInfo) {
        dispatch(storeDefaultInformation(defaultInfo));
      }

      // Store file ID
      if (file?.file_id) {
        dispatch(storeFileId({ file_id: file.file_id }));
      }

      // Store units/settings
      if (settings) {
        const unitInformation = {
          temperature: settings?.temperature,
          permeate_quality: settings?.permeate_quality,
          feed_quality: settings?.feed_quality,
          pressure: settings?.pressure,
          flow: settings?.flow,
          flux: settings?.flux,
          volume: settings?.volume,
          mass: settings?.mass,
          currency: settings?.currency,
          conversion_rate: settings?.conversion_rate,
          feed_water_permeate_factor: settings?.feed_water_permeate_factor,
          feed_water_factor: settings?.feed_water_factor,
        };
        dispatch(storeUnits(unitInformation));
      }

      const thresholdInformation = {
        max_feed_pressure: threshold?.max_feed_pressure,
        max_permeate_quality: threshold?.max_permeate_quality,
        std_warranty_period: threshold?.std_warranty_period,
        startup_pressure_drop: threshold?.startup_pressure_drop,
        designed_permeate_flow: threshold?.designed_permeate_flow,
        std_membrane_age: threshold?.std_membrane_age,
        max_pressure_drop: threshold?.max_pressure_drop,
        min_permeate_flow: threshold?.min_permeate_flow,
      };
      dispatch(storeReplacementThreshold(thresholdInformation));
    }

    return fileDetails.data.file.file_id;
  };

  const handleSwitchConfirm = async () => {
    setIsSwitchingTrain(true);
    const oldTrainNumber = parseInt(selectedTrain.split(" ")[1]);
    const newTrainNumber = parseInt(pendingTrain.split(" ")[1]);

    try {
      // First, get file_id without updating Redux (for validation)
      const newFileId = await fetchTrainDetails(newTrainNumber, false);
      const response = await getProceedToAnalysis(newFileId, newTrainNumber);
      if (response && response.status === 200) {
        // If validation succeeds, now update all Redux fields with new train data
        await fetchTrainDetails(newTrainNumber, true);
        dispatch(storeCleanData({ cleanData: null, cleanDate: null }));
        setSelectedTrain(pendingTrain);
        setShowSwitchPopup(false);
        createNotification(
          SUCCESS_NOTIFICATION,
          t("selectedTrainInfo.switchFromTo", {
            from: selectedTrain,
            to: pendingTrain,
          }),
          t("selectedTrainInfo.trainSwitchSuccess"),
        );
      } else {
        // If validation fails, revert to old train
        await fetchTrainDetails(oldTrainNumber, true);
        setPendingTrain(selectedTrain);
        setShowSwitchPopup(false);
        showErrorNotification();
      }
    } catch (error) {
      console.error("Failed to switch train:", error);
      try {
        // Revert to old train on error
        await fetchTrainDetails(oldTrainNumber, true);
      } catch (revertError) {
        console.error("Failed to revert to old train:", revertError);
      }
      setPendingTrain(selectedTrain);
      setShowSwitchPopup(false);
      showErrorNotification();
    } finally {
      setIsSwitchingTrain(false);
    }
  };

  const handleSwitchClose = (buttonId) => {
    if (buttonId === CustomModalButtons.CONFIRM) {
      handleSwitchConfirm();
    } else {
      setPendingTrain(selectedTrain);
      setShowSwitchPopup(false);
    }
  };

  return (
    <>
      <div className="train-selector-container">
        <div className="train-selector-label">
          {t("selectedTrainInfo.selectedTrainLabel")}
        </div>
        <div className="train-selector-dropdown">
          <WPDropdown
            id="train-selector"
            value={{ label: selectedTrain, value: selectedTrain }}
            options={Array.from({ length: num_trains || 1 }, (_, i) => ({
              label: `${t("ValueStrings.train")} ${i + 1}`,
              value: `${t("ValueStrings.train")} ${i + 1}`,
            }))}
            onChange={handleTrainChange}
            disabled={disabled}
          />
        </div>
      </div>

      {showSwitchPopup && (
        <ConfirmationPopup
          header={t("selectedTrainInfo.switchHeader", { train: pendingTrain })}
          type={DIALOG_TYPES.INFO}
          description={t("selectedTrainInfo.switchDescription")}
          onClose={handleSwitchClose}
          cancelBtn={t("ValueStrings.no")}
          confirmBtn={t("selectedTrainInfo.confirmBtn")}
          isLoading={isSwitchingTrain}
        />
      )}
    </>
  );
};

export default TrainSelector;
